/* eslint-disable @typescript-eslint/no-explicit-any */
import { Avatar, Box, Button, Center, Group, Image, Rating, SimpleGrid, Slider, Space, Stack, Table, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import { DefaultLayout } from "../../components/DefaultLayout/DefaultLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { IconArrowRight, IconBuildingStore, IconCheck, IconCircleCheck, IconList, IconListCheck, IconReceipt, IconTag, IconTruck, IconX } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../redux/hooks/hooks";
import './LandingPageStyle.css';
import LIBILogo from '../../assets/libi-logo.webp'
import { BackgroundVideo } from "./Components/BackgroundVideo";
import { InfiniteImageCarousel } from "../../components/InfiniteImageScrollCarousel/InfiniteImageScollCarousel";
import ShakingHands from '../../assets/contractor-hand-shake.png'
// Contractor Logos
import BaymenLogo from '../../assets/baymenLogo.jpeg';
import CareFreeLogo from '../../assets/careFreeLogo.png';
import JFRLogo from '../../assets/jfrLogo_black_noBackground.png';
import MetroLogo from '../../assets/metroLogo.png';
import RingerLogo from '../../assets/ringerLogo.png';
import KaiserBuiltLogo from '../../assets/kaiserBuiltLogo-black.png';
import TripleCrownLogo from '../../assets/tripleCrownLogo.png';
import VCLogo from '../../assets/vcLogo.png';
// Partner Logos
import QXOLogo from '../../assets/partner-logos/qxo-logo.png';
import EightFourLumberLogo from '../../assets/partner-logos/eightfour-lumber-logo.webp';
import RomaTileLogo from '../../assets/partner-logos/roma-tile-logo.png';
import AzekLogo from '../../assets/partner-logos/azek-logo.png';
import TimbertechLogo from '../../assets/partner-logos/timbertech-logo.png';
import SoundRiskManagementLogo from '../../assets/partner-logos/sound-risk-management-corp.png';
import SouthShoreSwagLogo from '../../assets/partner-logos/south-shore-swag-logo.jpeg';
import MrsElectricLogo from '../../assets/partner-logos/mrs-electric-logo.png';

export const LandingPage = () : JSX.Element => {
    /* Local State */
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = useAppSelector((state : any) => state.currentUser.currentUser);
    const [opened, { open, close }] = useDisclosure(false);
    const [sliderValue, setSliderValue] = useState(50000);
    const [savingsValue, setSavingsValue] = useState(5000);

    const signupFormRef = useRef<HTMLDivElement>(null);

    const scrollToSignupForm = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        const state = location.state as any;
        if (state?.scrollToTop) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (state?.scrollToForm) {
            setTimeout(() => {
                signupFormRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [location.state]);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://links.tradepulsecrm.com/js/form_embed.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const reviews = [
        {
            quote: "Method Materials makes ordering building materials easy, convenient, and significantly reduces costs. I saved thousands this year through the material savings and working with their insurance company.",
            name: "Garret Kaiser",
            title: "Owner, Kaiser Built General Contracting",
            rating: 5,
        },
        {
            quote: "I have reduced our material costs by roughly 10% across multiple suppliers. 5 stars.",
            name: "Kyle Kearney",
            title: "Owner, Triple Crown Exteriors",
            rating: 5,
        },
        {
            quote: "Excellent company for contractors looking to save money and grow their business. The group purchasing discounts and affiliate services have been extremely valuable for us.",
            name: "Joseph Vittini",
            title: "Owner, VC Legacy General Contracting",
            rating: 5,
        },
        {
            quote: "Method Materials helped our company save over $20,000 in material costs this past year through their purchasing network. On top of that, their insurance and accounting partners have streamlined a lot of our operations.",
            name: "Jillian Iorio",
            title: "Owner, Elevated Builders Co",
            rating: 5,
        },
        {
            quote: "Being part of Method Materials has been great for my business. Better pricing from suppliers, quality affiliate services, and a very professional team. Highly recommend for contractors.",
            name: "James Ringer",
            title: "Project Manager, Ringer Construction",
            rating: 5,
        },
        {
            quote: "Glad we joined Method Materials. Better pricing from suppliers and they've helped connect us with some useful services too.",
            name: "Tom Martin",
            title: "Owner, Metropolitan Roofing Systems",
            rating: 5,
        },
    ];

    const metricsData = [
        { icon: <IconCircleCheck size={56}/>, title: 'Reduced Prices on Materials', description: 'Pay less for the same materials through group pricing.' },
        { icon: <IconCircleCheck size={56}/>, title: 'A Community of Knowledge', description: 'Learn from other contractors, share insights, and avoid costly mistakes.' },
        { icon: <IconCircleCheck size={56}/>, title: 'Zero Risk', description: 'Free to join. Keep your existing supplier relationships.' },
    ];

    const whySwitchData = [
        {feature: 'Bulk Pricing', methodMaterials: <IconCheck color="#cc7800"/>, diy: <IconX color="red"/>},
        {feature: 'All-In-One Shop', methodMaterials: <IconCheck color="#cc7800"/>, diy: <IconX color="red"/>},
        {feature: 'Leverage Industry Connections', methodMaterials: <IconCheck color="#cc7800"/>, diy: <IconX color="red"/>},
        {feature: 'Local Supplier Network', methodMaterials: <IconCheck color="#cc7800"/>, diy: 'Varies'},
    ];

    const contractorImages = [
        BaymenLogo,
        CareFreeLogo,
        MetroLogo,
        VCLogo,
        RingerLogo,
        TripleCrownLogo,
        KaiserBuiltLogo,
        JFRLogo
    ];

    const rows = whySwitchData.map((elem) => (
        <Table.Tr key={elem.feature}>
            <Table.Td>{elem.feature}</Table.Td>
            <Table.Td>{elem.methodMaterials}</Table.Td>
            <Table.Td>{elem.diy}</Table.Td>
        </Table.Tr>
    ));

    const convertAnnualMaterialSpendToSavings = (annualSpend: number) : number => {
        return sliderValue * 0.10;
    }

    return(
        <DefaultLayout showNavBar={true} showSearchBar={true} showFooter={true}>
            {/* Hero Section */}
            <Box>
                <Box style={{ position: 'relative', height: '100%', paddingTop: '4em', paddingBottom: '4em' }}>
                    <BackgroundVideo />
                    <Box style={{ position: 'relative', zIndex: 1, height: '100%'}}>
                        <SimpleGrid cols={{ base: 1, md: 2 }}>
                            {/* Hero Text */}
                            <Center pl={'5%'} pr={'5%'}>
                                <Box style={{ position: 'relative', display: 'inline-block'}}>
                                    <Stack pl={'3%'} align="flex-start">
                                          <Box
                                            style={{
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            display: 'inline-block',
                                            }}
                                        >
                                        <Group wrap="nowrap" style={{ backgroundColor: 'rgba(232, 183, 114, 0.5)', padding: '0.5em', paddingRight: '1em', borderRadius: '20em', border: '1px solid #cc7800' }}>
                                            <IconCircleCheck color="#cc7800" />
                                            <Text fz={14} span>Free to Join. No Minimum Orders.</Text>
                                        </Group>
                                        </Box>
                                        <Box w={'80%'}>
                                            <Box visibleFrom="md">
                                                <div>
                                                    <Text fw={'bold'} fz={60} c={'#090b0f'}>Build a More Profitable Construction Business</Text>
                                                </div>
                                            </Box>
                                            <Box hiddenFrom="md">
                                               <div>
                                                    <Text fw={'bold'} fz={40} c={'#090b0f'}>Build a More Profitable Construction Business</Text>
                                                </div>
                                            </Box>
                                        </Box>
                                        <Box visibleFrom="md" w={'90%'}>
                                            <Text fz={20} c={'#5d636f'}>Join a network of contractors leveraging collective buying power, strategic partnerships, and business resources to reduce costs, increase margins, and grow smarter.</Text>
                                        </Box>
                                        <Box hiddenFrom="md" w={'90%'}>
                                            <Text fz={20} c={'#5d636f'}>Join a network of contractors leveraging collective buying power, strategic partnerships, and business resources to reduce costs, increase margins, and grow smarter.</Text>
                                        </Box>
                                        <Space h={'0.5em'} />
                                        <Box hiddenFrom="md">
                                            {currentUser === null && (
                                                <Button style={{borderRadius: '10px'}} className="tile-shadow" size={'xl'} fz={18} c={'white'} color={'#cc7800'} rightSection={<IconArrowRight />} onClick={scrollToSignupForm}>
                                                    Join For Free
                                                </Button>
                                            )}
                                        </Box>
                                        <Space h={'0.5em'} />
                                        <Group wrap="nowrap" w={'auto'}>
                                            <Group p={'1em'} className="tile-shadow" style={{borderRadius: '1em', backgroundColor: 'rgba(255, 255, 255, 0.3)'}}>
                                                <Center>
                                                    <Text fz={12}>Proud Member of</Text>
                                                </Center>
                                                <Image src={LIBILogo} h={'1.5em'} w={'8em'}/>
                                            </Group>
                                        </Group>
                                    </Stack>
                                </Box>
                            </Center>

                            {/* Hero Signup Form */}
                            <Center pl={'5%'} pr={'5%'} pt={'2em'} pb={'2em'}>
                                <Box
                                    ref={signupFormRef}
                                    id="signup-form"
                                    className='hover-shadow'
                                    style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.97)',
                                        borderRadius: '1.2em',
                                        border: '1px solid rgba(136, 137, 138, 0.2)',
                                        width: '100%',
                                        maxWidth: '480px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <iframe
                                        src="https://links.tradepulsecrm.com/widget/form/McbPjimZcIHTjpdXNulS"
                                        style={{ width: '100%', height: '1309px', border: 'none', borderRadius: '20px', display: 'block' }}
                                        id="inline-McbPjimZcIHTjpdXNulS"
                                        data-layout='{"id":"INLINE"}'
                                        data-trigger-type="alwaysShow"
                                        data-trigger-value=""
                                        data-activation-type="alwaysActivated"
                                        data-activation-value=""
                                        data-deactivation-type="neverDeactivate"
                                        data-deactivation-value=""
                                        data-form-name="Website Contact Form"
                                        data-height="1309"
                                        data-layout-iframe-id="inline-McbPjimZcIHTjpdXNulS"
                                        data-form-id="McbPjimZcIHTjpdXNulS"
                                        title="Website Contact Form"
                                    />
                                </Box>
                            </Center>
                        </SimpleGrid>
                    </Box>
                </Box>
            </Box>

            {/* Metric Section */}
            <Box className="metrics-section">
                <Space h={'2em'}/>
                <Center>
                        <Box visibleFrom="md">
                            <div>
                                <Text fw={'bold'} fz={44} c={'#090b0f'}>Join Now For Free</Text>
                            </div>
                        </Box>
                        <Box hiddenFrom="md">
                           <div>
                                <Text fw={'bold'} fz={30} ta={'center'} c={'#090b0f'}>Join Now For Free</Text>
                            </div>
                    </Box>
                </Center>
                <div className="metrics-container">
                    <Space h={'3em'} />
                    <Center>
                    <SimpleGrid w="100%" cols={{ base: 1, md: 3 }} spacing="xl" style={{ alignItems: 'stretch', maxWidth: '1200px', margin: '0 auto' }}>
                {
                    metricsData.map((stat, index) => (
                        <Box
                            key={index}
                            style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box
                                className="hover-shadow"
                                p="1.5em"
                                style={{
                                    borderRadius: '1em',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    border: '1px solid rgba(136, 137, 138, 0.2)',
                                    backgroundColor: 'white',
                                }}>
                                <Group wrap="nowrap" align="center">
                                    <Box style={{ flexShrink: 0, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {stat.icon}
                                    </Box>
                                    <Stack gap="xs">
                                        <Text c="#cc7800" fz={20} fw="bold">
                                            {stat.title}
                                        </Text>
                                        <Text c="#5d636f">
                                            {stat.description}
                                        </Text>
                                    </Stack>
                                </Group>
                            </Box>
                        </Box>
                    ))
                }
                    </SimpleGrid>
                    </Center>
                    <Space h={'3em'} />
                </div>
            </Box>
            <Space h={'8em'} />

            {/* Calculator Section */}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
                <Box pl={'5%'} pr={'5%'}>
                    <Text visibleFrom="md" fw={'bold'} fz={44}>Discover How Much Money You Could Save With Method Materials</Text>
                    <Text hiddenFrom="md" fz={30}>Discover how much money you could save with Method Materials</Text>
                    <Space h={'4em'} />
                    <Group justify={'space-between'}>
                        <Text c={'#5d636f'}>Amount spent on materials annually ($)</Text>
                        <Text c={'#5d636f'}>{`$${sliderValue.toLocaleString()}`}</Text>
                    </Group>
                    <Space h={'2em'} />
                    <Slider
                        value={sliderValue}
                        min={50000}
                        max={2999999}
                        thumbSize={34}
                        color={'#cc7800'}
                        label={null}
                        onChange={setSliderValue} />
                </Box>
                <Space hiddenFrom="md" h={'4em'} />
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Stack w="100%" gap="xs">
                        <Box style={{ textAlign: 'center' }}>
                            <Text >Yearly savings in material costs</Text>
                            <Text fs="italic" fz={14} c={'#5d636f'}>That's money you get to keep</Text>
                        </Box>
                        <Box style={{ textAlign: 'center' }}>
                            <Text fz={60}>${convertAnnualMaterialSpendToSavings(sliderValue).toLocaleString()}</Text>
                        </Box>
                    </Stack>
                </Box>
            </SimpleGrid>
            <Space h={'8em'} />

            {/* Contractors Section */}
            <div className="full-bleed partners-bleed">
                <div className="full-bleed-container">
                    <Box pt={'4em'} pb={'4em'}>
                        <Text hiddenFrom="md" pr={'5%'} fz={30}>Trusted by Top Contractors</Text>
                        <Text visibleFrom="md" fw={'bold'} pr={'5%'} fz={44}>Trusted by Top Contractors</Text>
                        <Space h={'2em'} />
                        <InfiniteImageCarousel images={contractorImages} reverseSlide={false} />
                        <Space h={'1em'} />
                        <Text c={'#5d636f'} >Forward-thinking companies gaining a competitive edge…</Text>
                        <Space h={'4em'} />
                    </Box>
                </div>
            </div>

            {/* Partners Section */}
            <Box className="benefits-section">
            <Space h="4em" />
            <Center pl="10%" pr="10%">
                <Text hiddenFrom="md" c="#1F2937" ta="center" fz={30} fw="bold">Partners</Text>
                <Text visibleFrom="md" c="#1F2937" ta="center" fz={44} fw="bold">Partners</Text>
            </Center>
            <Center>
                <Text c="#5d636f" ta="center" pl={'10%'} pr={'10%'}>Our network of suppliers and professional services</Text>
            </Center>
            <Space h="4em" />
            <SimpleGrid ml={'5%'} mr={'5%'} cols={{ base: 2, md: 4 }} spacing="xl" style={{ alignItems: 'stretch' }}>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={QXOLogo} style={{ maxWidth: '100%', height: '70px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={EightFourLumberLogo} style={{ maxWidth: '100%', height: '70px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={RomaTileLogo} style={{ maxWidth: '100%', height: '120px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={AzekLogo} style={{ maxWidth: '100%', height: '70px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={TimbertechLogo} style={{ maxWidth: '100%', height: '120px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={SoundRiskManagementLogo} style={{ maxWidth: '100%', height: '80px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={SouthShoreSwagLogo} style={{ maxWidth: '100%', height: '100px', objectFit: 'contain' }} />
                </Box>
                <Box className="hover-shadow" p="sm" style={{ borderRadius: '1em', border: '1px solid rgba(136, 137, 138, 0.2)', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                    <img src={MrsElectricLogo} style={{ maxWidth: '100%', height: '90px', objectFit: 'contain' }} />
                </Box>
            </SimpleGrid>
            <Space h="2em" />
            <Center>
                <Group gap="xs">
                    <Text c="#5d636f" fz={14} fs="">New partnerships added monthly</Text>
                    <Text c="#cc7800" fz={14}>
                        <span className="coming-soon-dot">•</span>
                        <span className="coming-soon-dot">•</span>
                        <span className="coming-soon-dot">•</span>
                    </Text>
                </Group>
            </Center>
            <Space h="8em" />
            </Box>

            {/* Reviews Carousel */}
            <div className="full-bleed kaiser-bleed">
                <div className="full-bleed-container">
                    <Box pt={'4em'} pb={'4em'} style={{ overflow: 'hidden' }}>
                        <Center pl="10%" pr="10%">
                            <Text hiddenFrom="md" c="#1F2937" ta="center" fz={30} fw="bold">What Our Members Are Saying</Text>
                            <Text visibleFrom="md" c="#1F2937" ta="center" fz={44} fw="bold">What Our Members Are Saying</Text>
                        </Center>
                        <Center>
                            <Text c="#5d636f" ta="center" pl={'10%'} pr={'10%'}>Did we mention it's free...</Text>
                        </Center>
                        <Space h={'6em'} />
                        <div className="review-track">
                            {[...reviews, ...reviews].map((review, i) => (
                                <Box
                                    key={i}
                                    p="xl"
                                    style={{
                                        width: '380px',
                                        flexShrink: 0,
                                        backgroundColor: 'white',
                                        borderRadius: '1em',
                                        border: '1px solid #E5E7EB',
                                    }}
                                >
                                    <Stack>
                                        <Rating color={'#cc7800'} value={review.rating} readOnly size="md" />
                                        <Text c={'#5d636f'}>"{review.quote}"</Text>
                                        <Group>
                                            <Avatar name={review.name} color="#cc7800" size={'md'} />
                                            <div>
                                                <Text c={'#585a5e'} fw={'bold'} fz={18}>{review.name}</Text>
                                                <Text c={'#5d636f'} fz={14}>{review.title}</Text>
                                            </div>
                                        </Group>
                                    </Stack>
                                </Box>
                            ))}
                        </div>
                    </Box>
                    <Space h={'2em'} />
                </div>
            </div>

            {/* Materials Band */}
            <Box className="materials-band-background" pt={'2.5em'} pb={'2.5em'} pl={'5%'} pr={'5%'}>
                <Center>
                    <Text hiddenFrom="md" fz={30} fw="bold" c="white">Save On</Text>
                    <Text visibleFrom="md" fz={40} fw="bold" c="white">Save On</Text>
                </Center>
                <Space h={'1em'} />
                <Group justify="center" gap={'2.5em'} wrap="wrap">
                    {['Roofing', 'Siding', 'Framing', 'Windows', 'Doors', 'Decking', 'Interior Trim', 'Exterior Trim'].map((material) => (
                        <Group key={material} gap={'0.5em'} wrap="nowrap">
                            <IconCheck color="#cc7800" />
                            <Text c="white" fw={''} style={{ whiteSpace: 'nowrap' }}>{material}</Text>
                        </Group>
                    ))}
                </Group>
                <Space h={'2em'} />
                <Center>
                    <Text fw={''} ta={'center'} c={'white'}>By working directly with us or one of our affiliate suppliers</Text>
                </Center>
            </Box>

            {/* About Us Section */}
            <Space h={'6em'} />
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0} ml={'5%'} mr={'5%'} mb={'6em'}>
                <Box style={{ borderRadius: '2em' }}>
                    <Center>
                        <Box style={{ borderRadius: '2em' }}>
                            <Image src={ShakingHands} visibleFrom="md" style={{ height: '24em', borderRadius: '1em'}} />
                            <Image src={ShakingHands} hiddenFrom="md" style={{ height: '16em', borderRadius: '1em'}} />
                        </Box>
                    </Center>
                </Box>
                <Space hiddenFrom="md" h="3em" />
                <Box style={{ display: 'flex', alignItems: 'center' }} pr={{ base: 0, md: '4em' }}>
                    <Stack gap="lg">
                        <Text hiddenFrom="md" fz={30} fw="bold" c="#1F2937">About Us</Text>
                        <Text visibleFrom="md" fz={44} fw="bold" c="#1F2937">About Us</Text>
                        <Text c={'#5d636f'}>Method Materials brings contractors together to create stronger buying power, lower material costs, and greater business opportunities. Through our growing network, members gain access to discounted supplier pricing, trusted professional services, shared project opportunities, and a collaborative community of contractors.</Text>
                        <Text c={'#5d636f'}>Every part of the Method Materials ecosystem is designed with one goal in mind: helping construction companies increase profitability. From reducing overhead and operational costs to creating new revenue opportunities, we help contractors keep more money in their business and scale more efficiently.</Text>
                        <Box>
                            <Button size="lg" color="#cc7800" rightSection={<IconArrowRight />} onClick={scrollToSignupForm}>
                                Join For Free
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </SimpleGrid>
        </DefaultLayout>
    );
}
